import { CloseButtonEvent } from '@acies/ui';
import { Effect, Exit, FiberHandle, FiberMap, Result, Schema, Scope } from 'effect';
import {
	answeredOf,
	Configuration,
	findGeometryParameter,
	GeometryParameter,
	type IConfigurationParameter,
	type IPresentedParameter,
	type ISentConfigurationParameter,
	isStockParameter,
	type IStockConfigurationParameter,
	LookupParameter,
	PickedGeometryParameter,
	type StockConfiguration,
	StockNumberParameter,
	withDefaultAnswers,
} from '../../domain/configurations';
import { parseDrawing } from '../../domain/drawings';
import { measurementsOf } from '../../domain/geometry-shapes';
import { type IPositions, ModelPosition, StockPosition } from '../../domain/positions';
import { SideTypes } from '../../domain/side-types';
import { createCalculationEffect, type PricedPosition } from '../../services/calculations';
import { createConfigurationEffect, createConfigurationFromEffect } from '../../services/configurations';
import { getDrawingStylesEffect } from '../../services/drawing-styles';
import { getDrawingEffect } from '../../services/drawings';
import { failureMessage, type PositionCallError } from '../../services/failures';
import { type ApiServices, callRuntime } from '../../services/runtime';
import { listCached } from '../../services/search';
import { createStockConfigurationEffect, createStockConfigurationFromEffect } from '../../services/stock-configurations';
import AnswerItemRendererEvent from './answers-section/answer-item-renderer-event';
import ItemConfigurationRendererEvent from './answers-section/item-configuration-renderer-event';
import type Configurator from './configurator';
import { ConfiguratorModel } from './configurator.model';
import GeometryMeasurementInputEvent from './dialogs/geometry-measurement-input-event';
import GeometrySelectListEvent from './dialogs/geometry-select-list-event';
import FrontBackToggleButtonBarEvent from './drawing-section/front-back-toggle-button-bar-event';
import ShowGeometryDialogButtonEvent from './drawing-section/show-geometry-dialog-button-event';
import CreatePositionRendererEvent from './positions-section/create-position-renderer-event';
import PositionItemRendererEvent, { type PositionChanges } from './positions-section/position-item-renderer/position-item-renderer-event';
import PositionsListEvent from './positions-section/positions-list-event';
import PropertiesPanelShowHideButtonEvent from './top-bar/properties-panel-show-hide-button-event';

/**
 * How long a run of selections is gathered into one drawing, in milliseconds.
 *
 * Long enough to cover a held arrow key, which repeats about every thirty milliseconds, and short enough that the drawing has settled by the time anyone has finished looking down at the list. Only the last drawing of a run waits this long: the first is shown at once.
 */
const DRAWING_BURST_WINDOW = 150;

/** Whether a side a row handed over is one of the sides there are. */
const isSideType = Schema.is(SideTypes);

/** Whether the position is one the engine prices — a model or a stock position — and so carries a BOM and an error of its own. */
function isPriced(position: IPositions | null): position is PricedPosition {
	return position instanceof ModelPosition || position instanceof StockPosition;
}

/**
 * The position with what the user changed in its row written in, as the kind it is.
 *
 * A stock position is only placed and counted — its row offers no width or height — so only those two are read off the changes for one, and nothing it does not hold is written to it.
 *
 * A stock position's total is counted again here, from the price of one: the engine cannot price a stock item yet, so nothing it answers will put the total right afterwards, and a position that holds a price without a configuration — every one the seed holds — is not even sent. A model's total is left to the
 * calculation its change asks for.
 */
function withChanges(current: PricedPosition, changes: PositionChanges): PricedPosition {
	if (current instanceof ModelPosition) {
		return new ModelPosition({ ...current, ...changes });
	}
	const { position = current.position, quantity = current.quantity } = changes;
	return new StockPosition({ ...current, position, quantity, totalPrice: quantity * current.price });
}

/**
 * A stock parameter with the user's answer on it, or null when it takes none from a row.
 *
 * One the engine says may not be changed takes none — its row is read-only, so null here is an event made somewhere other than a row. A number takes the text as the number it reads as, and nothing, or something that is not a number, is a change it cannot have and is sent as nothing. Text is taken as it is.
 */
function stockAnswer(parameter: IStockConfigurationParameter, value: string): IStockConfigurationParameter | null {
	if (parameter.isReadOnly) {
		return null;
	}
	if (parameter instanceof StockNumberParameter) {
		const number = value === '' ? Number.NaN : Number(value);
		return Number.isFinite(number) ? parameter.withValue(number) : null;
	}
	return parameter.withValue(value);
}

/**
 * What the Configurator does, as opposed to what it looks like.
 *
 * The components put themselves together and say what happened; deciding what that means is this class's job. Keeping the two apart means a component can be read for its layout alone, and the decisions can be read without the layout in the way.
 *
 * It is not a state machine yet. It will become one, and the shape is already the one a state machine wants: everything that happens arrives here as an event, and everything that changes changes through the model.
 *
 * The Configurator is only a type here. Importing it for real would be a cycle, since it is the Configurator that builds the machine.
 */
export default class ConfiguratorMachine {
	/** How many calculations are being worked out for each position, keyed by its id. A position with none out has no entry. See {@link ConfiguratorMachine.counting}. */
	#calculationsOut: Map<string, number>;

	/** How many configurations are being asked for for each position, keyed by its id. A position with none out has no entry. See {@link ConfiguratorMachine.counting}. */
	#configurationsOut: Map<string, number>;

	#configurator: Configurator;

	/**
	 * The fiber loading the drawing that belongs on screen, if one is.
	 *
	 * A FiberHandle holds one fiber and interrupts it when the next one is run in it, which is exactly what a drawing that is no longer wanted is: the fetch out for it is aborted, and nothing it would have written is written. Lives in {@link ConfiguratorMachine.drawingScope}.
	 */
	#drawingLoad: FiberHandle.FiberHandle;

	/**
	 * Where the drawing's fibers live, so {@link stop} can end them all at once.
	 *
	 * A drawing is for the screen alone, so a Configurator taken out of the document has no use for one on its way: closing this interrupts the gathering window and any load out. The work on positions is not in here — see {@link ConfiguratorMachine.positionWork}.
	 */
	#drawingScope: Scope.Closeable;

	/**
	 * The fiber that closes the gathering window, if the window is open.
	 *
	 * Run again on every selection inside the window, which interrupts the one before it, so the window closes {@link DRAWING_BURST_WINDOW} after the last selection of a run rather than after the first.
	 */
	#drawingWindow: FiberHandle.FiberHandle;

	/**
	 * Whether the gathering window is open, which is whether the next selection only moves the target.
	 *
	 * Kept beside the fiber rather than read off it, because it has to be answered synchronously, as the selection arrives. Set when the window opens, and cleared only by the window closing on its own: a window interrupted by the next selection is replaced by one that is open too.
	 */
	#drawingWindowOpen: boolean;

	/**
	 * The position the drawing on screen, or on its way, was loaded for — which is null for the empty section a de-selection loads — or undefined when nothing is loaded or on its way.
	 *
	 * What keeps the end of a gathering window from loading again what the beginning of it already loaded. A single selection opens a window too — there is no telling a single one from the first of a run until the run does not come — and without this it would fetch the same drawing a second time when the window
	 * closed.
	 *
	 * Undefined is kept apart from null for the one case that needs it: a load interrupted because the target moved has loaded nothing, and whatever is wanted when the window closes — de-selecting included — has to be loaded then.
	 */
	#loadedDrawingPosition: IPositions | null | undefined;

	/**
	 * The configuration of its item last picked for a stock position whose configuration has not come back yet, by its code and keyed by the position's id.
	 *
	 * What a count changed meanwhile is sent in — see #workAfterChange — rather than the configuration the position still holds, which names the one the user has just left. Cleared once the API has answered either way, for the reason {@link ConfiguratorMachine.sentStockItems} is.
	 */
	#pickedItemConfigurations: Map<string, string>;

	/**
	 * The new stock positions whose item's configurations are being listed, so the only one can be picked for the user — see #pickOnlyItemConfigurationOr — by their ids.
	 *
	 * Kept until the list has been answered, so a change to the row made meanwhile, which interrupts the listing, lists them again rather than leaving the position waiting on a pick there is nothing to choose between — see #workAfterChange. Cleared as well once any configuration has come back for the position, for the
	 * reason {@link ConfiguratorMachine.sentStockItems} is.
	 */
	#listingItemConfigurations: Set<string>;

	/**
	 * The work going on for each position, keyed by its id: being configured, priced, or both one after the other.
	 *
	 * One fiber per position, and running another for the same position interrupts the one it has. That is the whole answer to two changes made one after the other: the first is not left to land after the second and overwrite it, it is stopped where it is — the request out aborted, the engine no longer asked after a
	 * price — and the second carries on from what the first sent. See {@link ConfiguratorMachine.updateParameter}.
	 *
	 * Per position rather than one for the Configurator, because positions are worked on side by side: a change to one must not stop another being priced.
	 *
	 * Not closed by {@link stop}. The model is what this work writes to, and a position the engine has priced is not made wrong by the screen having gone; stopping it half way would leave rows turning for a price nobody will read.
	 */
	#positionWork: FiberMap.FiberMap<string>;

	/** The runtime the work and the drawings run in, which is the one every other call of the Configurator runs in. */
	#runtime = callRuntime.runtime;

	/**
	 * The parameters last sent for a position whose configuration has not come back yet, keyed by its id.
	 *
	 * What the next change to the position is built from, rather than the configuration it holds: a change that was interrupted before the API answered is in here and in nothing else, and building the next one from the configuration would lose it. Cleared once the API has answered either way, since what comes back is
	 * then the truth — or, refused, the position is as it was, carrying the refusal.
	 */
	#sentItems: Map<string, ReadonlyArray<ISentConfigurationParameter>>;

	/** The stock parameters last sent for a stock position whose configuration has not come back yet, keyed by its id. What {@link ConfiguratorMachine.sentItems} is for a model, and kept apart since the two families of parameter are sent apart. */
	#sentStockItems: Map<string, ReadonlyArray<IStockConfigurationParameter>>;

	/** Whether {@link stop} has been called. */
	#stopped: boolean;

	/**
	 * The drawing that belongs on screen, or null when none does.
	 *
	 * What the gathering window loads when it closes. The position itself rather than its drawing id, and the machine's own record of it rather than the selection: `currentPosition` holds the object that was selected, and a position that has just been priced is a new object.
	 */
	#wantedDrawingPosition: IPositions | null;

	/** The Configurator this machine listens to and acts on. */
	public get configurator(): Configurator {
		return this.#configurator;
	}

	/** Whether {@link stop} has been called. A stopped machine stays stopped. */
	public get stopped(): boolean {
		return this.#stopped;
	}

	/**
	 * Stops the machine: nothing further is listened for, and nothing is loaded for the screen.
	 *
	 * For a Configurator taken out of the document, and for a test that built a machine of its own. Without it the machine outlives what it drove: the gathering window fires up to 150 milliseconds later and fetches a drawing through whatever fetch is installed by then, and a load already out writes its answer into a
	 * model nothing is showing — which is exactly what a test running next sees.
	 *
	 * The drawing's fibers are interrupted by closing their scope, so a load already out is aborted rather than left to land. A price or a configuration already on its way is not: the model is what those write to, and a position the engine has priced is not made wrong by the screen having gone — see
	 * {@link ConfiguratorMachine.positionWork}.
	 *
	 * Terminal rather than reversible, since initialize() builds a machine once and nothing restarts one: a Configurator put back into the document keeps the machine it had, stopped. Calling it twice does nothing the first time did not.
	 */
	public stop(): void {
		if (this.#stopped) {
			return;
		}
		this.#stopped = true;
		this.#removeEventListeners();
		// forked rather than run in place: an interrupted fetch ends a moment later, and closing waits
		// for it. The interruption itself is immediate, so nothing the load would have written is
		this.#runtime.runFork(Scope.close(this.#drawingScope, Exit.void));
		this.#drawingWindowOpen = false;
		this.#wantedDrawingPosition = null;
		this.#loadedDrawingPosition = undefined;
	}

	public constructor(configurator: Configurator) {
		this.#calculationsOut = new Map();
		this.#configurationsOut = new Map();
		this.#configurator = configurator;
		// Made here, synchronously, so there is something to run work in from the first event on. Made
		// in the default runtime, since a map, a handle and a scope need none of the Configurator's
		// services — the work run in them does, and gets them from #runtime.
		this.#drawingScope = Scope.makeUnsafe();
		this.#drawingLoad = Effect.runSync(Scope.provide(FiberHandle.make(), this.#drawingScope));
		this.#drawingWindow = Effect.runSync(Scope.provide(FiberHandle.make(), this.#drawingScope));
		this.#drawingWindowOpen = false;
		this.#listingItemConfigurations = new Set();
		this.#loadedDrawingPosition = undefined;
		this.#pickedItemConfigurations = new Map();
		this.#positionWork = Effect.runSync(Scope.provide(FiberMap.make<string>(), Scope.makeUnsafe()));
		this.#sentItems = new Map();
		this.#sentStockItems = new Map();
		this.#stopped = false;
		this.#wantedDrawingPosition = null;
		// the listeners are registered on the Configurator, so they would otherwise run in its scope
		// rather than in this one
		this.answerChanged = this.answerChanged.bind(this);
		this.drawingSideChanged = this.drawingSideChanged.bind(this);
		this.showGeometryDialogButtonClicked = this.showGeometryDialogButtonClicked.bind(this);
		this.propertiesPanelShowHideButtonClicked = this.propertiesPanelShowHideButtonClicked.bind(this);
		this.closeButtonClicked = this.closeButtonClicked.bind(this);
		this.geometryChanged = this.geometryChanged.bind(this);
		this.itemConfigurationChanged = this.itemConfigurationChanged.bind(this);
		this.measurementChanged = this.measurementChanged.bind(this);
		this.positionCreated = this.positionCreated.bind(this);
		this.positionChanged = this.positionChanged.bind(this);
		this.positionSelectionChanged = this.positionSelectionChanged.bind(this);
		this.#addEventListeners();
	}

	/**
	 * The row that fills in a new position and the list a position is picked in are both buried in the sections, and their events bubble.
	 *
	 * Listening at the Configurator rather than passing the model down means no section has to carry an event it has nothing to do with.
	 */
	#addEventListeners(): void {
		this.configurator.addEventListener(AnswerItemRendererEvent.ANSWER_CHANGED, this.answerChanged as EventListener);
		this.configurator.addEventListener(AnswerItemRendererEvent.SIDE_CHANGED, this.answerChanged as EventListener);
		this.configurator.addEventListener(CreatePositionRendererEvent.POSITION_CREATED, this.positionCreated as EventListener);
		this.configurator.addEventListener(PositionItemRendererEvent.POSITION_CHANGED, this.positionChanged as EventListener);
		this.configurator.addEventListener(PositionsListEvent.POSITION_SELECTION_CHANGED, this.positionSelectionChanged as EventListener);
		this.configurator.addEventListener(FrontBackToggleButtonBarEvent.DRAWING_SIDE_CHANGED, this.drawingSideChanged as EventListener);
		this.configurator.addEventListener(ShowGeometryDialogButtonEvent.SHOW_GEOMETRY_DIALOG_BUTTON_CLICKED, this.showGeometryDialogButtonClicked);
		this.configurator.addEventListener(PropertiesPanelShowHideButtonEvent.CLICKED, this.propertiesPanelShowHideButtonClicked);
		this.configurator.addEventListener(CloseButtonEvent.CLOSE_BUTTON_CLICKED, this.closeButtonClicked as EventListener);
		this.configurator.addEventListener(GeometrySelectListEvent.GEOMETRY_CHANGED, this.geometryChanged as EventListener);
		this.configurator.addEventListener(GeometryMeasurementInputEvent.MEASUREMENT_CHANGED, this.measurementChanged as EventListener);
		this.configurator.addEventListener(ItemConfigurationRendererEvent.ITEM_CONFIGURATION_CHANGED, this.itemConfigurationChanged as EventListener);
	}

	/** The same ones, taken off again by {@link stop}. The listeners are the bound methods the constructor made, so they are the very functions that were added. */
	#removeEventListeners(): void {
		this.configurator.removeEventListener(AnswerItemRendererEvent.ANSWER_CHANGED, this.answerChanged as EventListener);
		this.configurator.removeEventListener(AnswerItemRendererEvent.SIDE_CHANGED, this.answerChanged as EventListener);
		this.configurator.removeEventListener(CreatePositionRendererEvent.POSITION_CREATED, this.positionCreated as EventListener);
		this.configurator.removeEventListener(PositionItemRendererEvent.POSITION_CHANGED, this.positionChanged as EventListener);
		this.configurator.removeEventListener(PositionsListEvent.POSITION_SELECTION_CHANGED, this.positionSelectionChanged as EventListener);
		this.configurator.removeEventListener(FrontBackToggleButtonBarEvent.DRAWING_SIDE_CHANGED, this.drawingSideChanged as EventListener);
		this.configurator.removeEventListener(ShowGeometryDialogButtonEvent.SHOW_GEOMETRY_DIALOG_BUTTON_CLICKED, this.showGeometryDialogButtonClicked);
		this.configurator.removeEventListener(PropertiesPanelShowHideButtonEvent.CLICKED, this.propertiesPanelShowHideButtonClicked);
		this.configurator.removeEventListener(CloseButtonEvent.CLOSE_BUTTON_CLICKED, this.closeButtonClicked as EventListener);
		this.configurator.removeEventListener(GeometrySelectListEvent.GEOMETRY_CHANGED, this.geometryChanged as EventListener);
		this.configurator.removeEventListener(GeometryMeasurementInputEvent.MEASUREMENT_CHANGED, this.measurementChanged as EventListener);
		this.configurator.removeEventListener(ItemConfigurationRendererEvent.ITEM_CONFIGURATION_CHANGED, this.itemConfigurationChanged as EventListener);
	}

	/**
	 * Puts a newly filled in position into the model, which is what the lists render from, and makes it the selected one.
	 *
	 * The position goes in before it has a price, so the row appears the moment it is created and says for itself that it is being worked out. Working it up is forked as the position's work rather than awaited: a listener cannot be waited on, and nothing here needs the answer except the model, which the work writes
	 * to.
	 */
	private positionCreated(event: CreatePositionRendererEvent): void {
		ConfiguratorModel.positions.addItem(event.position);
		// after it is in the model and not before: the list renders from the collection as it is
		// written to, so this is the first moment there is a row to scroll to. The list is sorted by
		// the place a position holds rather than by when it was made, so where the new row landed is
		// whatever its number says — usually the end, and not when the user typed a place of their own
		const { positionsList } = this.configurator.positionsSection;
		positionsList.scrollToItem(event.position);
		// the new position is the one on screen, as if it had been clicked. Selecting it moves no
		// focus — a row being read only toggles its selection on a click — so the user stays in the
		// creating row's model field, ready to type the next one
		positionsList.selectedItem = event.position;
		// a model and a stock position are both configured, and a model is priced as well. A stock
		// position is configured as its item alone: the configuration the item comes in is picked
		// afterwards, among the ones the engine answers with. A text position is neither configured
		// nor priced: the row hands it over done
		if (event.position instanceof ModelPosition) {
			this.#runForPosition(event.position, this.#workUpPosition(event.position));
		} else if (event.position instanceof StockPosition) {
			this.#runForPosition(event.position, this.#workUpStockPosition(event.position));
		}
	}

	/**
	 * Takes what the user changed in a model or stock position's row: its place in the list, its width, its height or its quantity — a stock position is only placed and counted.
	 *
	 * The change is written to the position at once, before anything is asked, so whatever reads the position next reads the change: a changed answer made while the API is still working on this one is sent with the new measurements rather than the old ones, and the row shows what the user typed rather than going back
	 * to what it had. The position is not marked pending yet. That waits for the configuration, as it does for a changed answer, so a refusal leaves the position saying what it said before, with the measurements its configuration holds — see #refuse.
	 *
	 * Only the measurements go to the API, since only they change what the engine builds; a new place in the list is the position's own and asks for nothing, save when work is still out for the position, which is then done again from the position as it now is. A position with no configuration, one whose configuring
	 * failed or has not yet been answered, is worked up again from the start.
	 *
	 * A place another position holds is ignored. The row refuses it before it is ever sent, so this is a change made somewhere other than the row.
	 *
	 * A new place is written by reconciling the positions rather than replacing the one, because the list is sorted by place and a replacement that lands elsewhere has the list build every row again: the row the user was in would leave the document, and the focus with it. Reconciled, the row is moved, and keeps both
	 * the focus and the selection.
	 *
	 * A stock position is counted again the way a model is measured again: its answers go back with the new count, and what the API makes of them is what it holds.
	 */
	private positionChanged(event: PositionItemRendererEvent): void {
		const { changes } = event;
		const all = ConfiguratorModel.positions.collection;
		const current = all.find((position) => position.id === event.position.id) ?? null;
		if (!isPriced(current)) {
			return;
		}
		if (changes.position !== undefined && all.some((position) => position.position === changes.position && position.id !== current.id)) {
			return;
		}
		const changed = withChanges(current, changes);
		if (changes.position === undefined) {
			ConfiguratorModel.positions.replace(current, changed);
		} else {
			ConfiguratorModel.positions.reconcile(
				all.map((position) => (position === current ? changed : position)),
				(position) => position.id,
			);
		}
		// a new place alone asks the API for nothing — unless work is still out for the position: it
		// would land on the position as it was, which the model no longer holds, and so land nowhere,
		// leaving the row turning for a price that never comes. That work is done again instead
		const measured = changes.width !== undefined || changes.height !== undefined || changes.quantity !== undefined;
		if (!measured && !FiberMap.hasUnsafe(this.#positionWork, current.id)) {
			return;
		}
		this.#runForPosition(changed, this.#workAfterChange(changed));
	}

	/**
	 * The work a changed position is given: its answers sent again as it now measures or counts, or, for a model that holds no configuration, worked up from the start.
	 *
	 * A stock position that holds no configuration is only counted: its change is its own and asks the API for nothing. That is every one the seed holds, and one whose configuring failed — the engine cannot price a stock item yet, so there is no price for a count to change either.
	 *
	 * A stock position with a configuration of its item picked and not yet answered is asked for in that one again, counted anew: sent with the answers it holds, it would go back to the configuration the user had just left.
	 *
	 * A new stock position whose item's configurations were still being listed has them listed again, and the only one picked for it, counted anew: asked for with the answers it holds, it would stay the item alone and wait on a pick there is nothing to choose between. An item with more than one is asked for with its
	 * defaults, as a new one is, and the new count.
	 */
	#workAfterChange(changed: PricedPosition): Effect.Effect<void, never, ApiServices> {
		if (changed instanceof ModelPosition) {
			return changed.configuration === null ? this.#workUpPosition(changed) : this.#remeasure(changed, changed.configuration);
		}
		if (changed.configuration === null) {
			return Effect.void;
		}
		const picked = this.#pickedItemConfigurations.get(changed.id);
		if (picked !== undefined) {
			return this.#pickItemConfiguration(changed, changed.configuration, picked);
		}
		if (this.#listingItemConfigurations.has(changed.id)) {
			const { items } = changed.configuration.parameters;
			return this.#pickOnlyItemConfigurationOr(changed, changed.configuration, this.#reconfigureStock(changed, changed.configuration, withDefaultAnswers(items) ?? items));
		}
		return this.#reconfigureStock(changed, changed.configuration, this.#sentStockItems.get(changed.id) ?? changed.configuration.parameters.items);
	}

	/**
	 * Answers a changed answer: the new value goes onto its parameter, the configuration goes back to the API, and what the API holds afterwards is what the position holds.
	 *
	 * A side picked for a lookup is answered the same way, and by this same listener — ANSWER_CHANGED and SIDE_CHANGED differ only in which field of the parameter is written, which is #changedParameter's to decide.
	 *
	 * The position is found by the parameter rather than read off the selection, and that is not caution for its own sake. A change replaces the position with a new one, and the selection goes on holding the old object — the machine matches by id everywhere for that reason. The rows are then re-dealt from the new
	 * configuration, so a second change arrives carrying a parameter of the new one; looked up on the selection it would be found on no configuration at all, and the second change would be dropped without a word. Found on the position that actually holds it, it lands.
	 *
	 * A refusal is shown on the row that gave the answer, and nothing is written: the position goes on holding what the API still holds, and the user's answer stays in the field to be corrected.
	 *
	 * Forked as the position's work, for the reason positionCreated gives, and interrupting whatever work the position already had — see {@link ConfiguratorMachine.positionWork}.
	 */
	private answerChanged(event: AnswerItemRendererEvent): void {
		const { parameter } = event;
		if (isStockParameter(parameter)) {
			this.#stockAnswerChanged(parameter, event.value);
			return;
		}
		const held = this.#positionHolding(parameter);
		if (held === null) {
			return;
		}
		const changed = this.#changedParameter(event, parameter);
		if (changed === null) {
			return;
		}
		this.#runForPosition(
			held.position,
			this.#updateParameter(held.position, held.configuration, parameter, changed, () => {
				this.#rejectAnswer(event);
			}),
		);
	}

	/**
	 * Answers a changed answer to a stock item's parameter, the way {@link ConfiguratorMachine.answerChanged} answers a model's: the new value goes onto the parameter, the configuration is asked for again with it, and what the API holds afterwards is what the position holds.
	 *
	 * Found on the stock position that holds the parameter, by identity, for the reason a model's parameter is. A change the parameter cannot have is sent as nothing — see {@link stockAnswer}.
	 *
	 * A refusal is written to the position and nothing else, the way a model's is, and the user's answer stays in its field to be corrected. Stock rows have no side to put back.
	 */
	#stockAnswerChanged(parameter: IStockConfigurationParameter, value: string): void {
		const held = this.#stockPositionHolding(parameter);
		if (held === null) {
			return;
		}
		const changed = stockAnswer(parameter, value);
		if (changed === null) {
			return;
		}
		this.#runForPosition(held.position, this.#updateStockParameter(held.position, held.configuration, parameter, changed));
	}

	/**
	 * Takes a configuration of its item the user picked for a stock position: the position is configured again in it, and what the API makes of that is what the position holds.
	 *
	 * The position is found by the configuration the pick was made on, the way a stock parameter finds its own — see #stockPositionHolding: the row above the answers is shown the configuration of the position being worked on, and a position that has since been given another is no longer the one the pick was made for,
	 * so nothing is asked.
	 *
	 * Forked as the position's work, for the reason answerChanged is, and interrupting whatever work the position already had: a pick made while an answer is out for the configuration being left is the user moving on from it.
	 */
	private itemConfigurationChanged(event: ItemConfigurationRendererEvent): void {
		const position = ConfiguratorModel.positions.collection.find((candidate): candidate is StockPosition => candidate instanceof StockPosition && candidate.configuration === event.configuration);
		if (position === undefined) {
			return;
		}
		this.#runForPosition(position, this.#pickItemConfiguration(position, event.configuration, event.itemConfiguration));
	}

	/** The stock position whose configuration holds that parameter, or null when none does — the way {@link ConfiguratorMachine.positionHolding} finds a model's. */
	#stockPositionHolding(parameter: IStockConfigurationParameter): { readonly configuration: StockConfiguration; readonly position: StockPosition } | null {
		for (const position of ConfiguratorModel.positions.collection) {
			if (position instanceof StockPosition && position.configuration !== null && position.configuration.parameters.items.includes(parameter)) {
				return { configuration: position.configuration, position };
			}
		}
		return null;
	}

	/**
	 * Asks the API for a stock configuration with one parameter changed, and puts what comes back on the position, the price included.
	 *
	 * The stock counterpart of {@link ConfiguratorMachine.updateParameter}, built the same way: on the set last sent for the position while the API has not answered it, or else on the configuration it holds, with the changed parameter matched by its path.
	 */
	#updateStockParameter(position: StockPosition, configuration: StockConfiguration, current: IStockConfigurationParameter, changed: IStockConfigurationParameter): Effect.Effect<void, never, ApiServices> {
		const sent = this.#sentStockItems.get(position.id) ?? configuration.parameters.items;
		const items = sent.map((parameter) => (parameter.self === current.self ? changed : parameter));
		return this.#reconfigureStock(position, configuration, items);
	}

	/**
	 * Sends a stock position's answers, counted as the position now is, and takes what the API makes of them.
	 *
	 * The stock counterpart of {@link ConfiguratorMachine.reconfigure}, ending the same ways. The item and its configuration are read off the configuration being answered, which names them whatever the position was made with, and the count off the position.
	 *
	 * A refusal leaves the position with what it had and the refusal beside it, counted as the configuration still is. Taken, the position is not priced: the engine cannot price a stock item yet, so a configuration it calls complete is the end of the work — see #settleStock — and one it calls pending waits on its
	 * answers, the way a model's does.
	 */
	#reconfigureStock(position: StockPosition, configuration: StockConfiguration, items: ReadonlyArray<IStockConfigurationParameter>): Effect.Effect<void, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			this.#sentStockItems.set(position.id, items);
			const request = { itemConfiguration: configuration.itemConfigurationKey, key: configuration.itemKey, quantity: position.quantity };
			const answered = yield* this.#takeStockConfiguration(position, configuration, createStockConfigurationFromEffect(request, answeredOf(items)));
			if (answered !== null) {
				this.#settleStock(answered.position);
			}
		});
	}

	/**
	 * Asks the API for a stock configuration again, in the configuration of the item the user picked, and takes what it makes of it.
	 *
	 * With no answers: the ones the position holds were to the questions another configuration asked, so the new one starts from whatever the engine says, as a stock position's first configuration does — and is then worked on the way that one is, its open questions answered with their defaults, see
	 * #answerDefaultsOrSettle. The item is read off the configuration being changed and the count off the position, for the reason #reconfigureStock reads them so.
	 *
	 * A refusal leaves the position with what it had and the refusal beside it, the way a refused answer does, and the row above the answers on the user's pick — see #showParameters.
	 *
	 * The pick is kept until the API has answered it, so a count changed meanwhile is sent in the configuration picked rather than the one being left — see #workAfterChange.
	 */
	#pickItemConfiguration(position: StockPosition, configuration: StockConfiguration, itemConfiguration: string): Effect.Effect<void, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			this.#sentStockItems.delete(position.id);
			this.#pickedItemConfigurations.set(position.id, itemConfiguration);
			const request = { itemConfiguration, key: configuration.itemKey, quantity: position.quantity };
			const answered = yield* this.#takeStockConfiguration(position, configuration, createStockConfigurationEffect(request));
			if (answered !== null) {
				yield* this.#answerDefaultsOrSettle(answered.position, answered.configuration);
			}
		});
	}

	/**
	 * Takes what the API made of a stock configuration asked for again, and answers with the position holding it — or null, for a refusal.
	 *
	 * The ending #reconfigureStock and #pickItemConfiguration share. A refusal leaves the position with what it had and the refusal beside it, counted as the configuration still is. Taken, the position holds the configuration, unpriced and pending until it is settled, and its answers and its error are on screen if it
	 * is the one being worked on.
	 */
	#takeStockConfiguration<E extends PositionCallError>(position: StockPosition, configuration: StockConfiguration, call: Effect.Effect<StockConfiguration, E, ApiServices>): Effect.Effect<{ readonly configuration: StockConfiguration; readonly position: StockPosition } | null, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			const held = yield* this.#attempting('configuration', position, call);
			// answered either way, so the next change starts from what the position holds now
			this.#sentStockItems.delete(position.id);
			this.#pickedItemConfigurations.delete(position.id);
			this.#listingItemConfigurations.delete(position.id);
			if (Result.isFailure(held)) {
				// counted back to what the configuration holds, and the total with it — see withChanges
				const refused = new StockPosition({ ...position, error: held.failure, quantity: configuration.quantity, totalPrice: configuration.quantity * position.price });
				ConfiguratorModel.positions.replace(position, refused);
				this.#showErrorIfSelected(refused);
				return null;
			}
			const answered = new StockPosition({ ...position, calculationId: null, configuration: held.success, error: null, status: 'pending' });
			ConfiguratorModel.positions.replace(position, answered);
			this.#showParametersIfSelected(answered);
			this.#showErrorIfSelected(answered);
			return { configuration: held.success, position: answered };
		});
	}

	/**
	 * Answers a shape the user picked in the geometry dialog: the geometry is cut to it, the configuration goes back to the API, and what the API holds afterwards is what the position holds — measurements, drawing and price.
	 *
	 * The way a changed answer is taken, with the geometry parameter as the answer. It is found among the parameters on show rather than on the selection, and its position by the parameter, for the reason answerChanged gives. None is a position with no geometry, which the dialog cannot be opened on; an event that
	 * arrives anyway has nothing to change.
	 *
	 * Forked as the position's work, for the reason answerChanged is. Picking another shape before the API has answered for the last interrupts it, so it is the shape picked last that the position ends up cut to.
	 *
	 * A refusal writes what the API said to the position and nothing else, as a refused answer does — see {@link ConfiguratorMachine.refuse} — and leaves the dialog on the shape the user picked rather than putting back the one the position is still cut to. The list follows the model, so it goes back by itself the next
	 * time the parameters on show change. The drawing is taken down, since it is of the shape the user has just left — see {@link ConfiguratorMachine.rejectGeometry} — and the refusal is shown in its place, read off the model.
	 */
	private geometryChanged(event: GeometrySelectListEvent): void {
		const geometry = findGeometryParameter(ConfiguratorModel.currentParameters.collection);
		if (geometry === null) {
			return;
		}
		const held = this.#positionHolding(geometry);
		if (held === null) {
			return;
		}
		this.#runForPosition(
			held.position,
			this.#updateParameter(held.position, held.configuration, geometry, PickedGeometryParameter.from(geometry, event.shapeType), () => {
				this.#rejectGeometry(held.position);
			}),
		);
	}

	/**
	 * Takes the drawing off the screen after the API would not take the shape the user picked, if the position is still the one on screen.
	 *
	 * The dialog stays on the picked shape, and a drawing of the shape the position is still cut to would say the opposite. Matched by id, for the reason the drawing is matched that way everywhere. A position the user has since left has no drawing on screen to take down, and one a stopped machine refuses for has no
	 * screen at all — see {@link stop}.
	 *
	 * A load still out for the position is interrupted, since left to land it would put the drawing straight back. The position is not replaced, so selecting it again loads its drawing as before.
	 */
	#rejectGeometry(position: ModelPosition): void {
		if (this.#stopped || ConfiguratorModel.currentPosition.value?.id !== position.id) {
			return;
		}
		this.#runtime.runFork(FiberHandle.clear(this.#drawingLoad));
		ConfiguratorModel.currentDrawing.value = null;
	}

	/**
	 * Answers a measurement the user gave another length in the geometry dialog: the geometry goes back to the API with that length on it, and what the API holds afterwards is what the position holds — its measurements, drawing and price.
	 *
	 * The way a picked shape is taken, with the geometry found the same way. The measurement is put on the geometry last sent for the position when there is one, rather than on the one on show — see #geometryToChange. A measurement the shape does not have, or a length no measurement can be, is a change the geometry
	 * cannot have, and is sent as nothing.
	 *
	 * A refusal writes what the API said to the position and nothing else, and takes back what the fields of every length the refused geometry changed had said — see #rejectMeasurement. The drawing is left up in the model, unlike after a refused shape: the position is still cut to the shape it was, with the
	 * measurements it had, and that is what the drawing section shows. The geometry dialog shows the refusal in the drawing's place meanwhile, and the drawing again once an answer has gone through.
	 */
	private measurementChanged(event: GeometryMeasurementInputEvent): void {
		const geometry = findGeometryParameter(ConfiguratorModel.currentParameters.collection);
		if (geometry === null) {
			return;
		}
		const held = this.#positionHolding(geometry);
		if (held === null) {
			return;
		}
		const changed = this.#geometryToChange(held.position, geometry).withMeasurement(event.measurement, event.value);
		if (changed === null) {
			return;
		}
		this.#runForPosition(
			held.position,
			this.#updateParameter(held.position, held.configuration, geometry, changed, () => {
				this.#rejectMeasurement(held.position, geometry, changed);
			}),
		);
	}

	/**
	 * The geometry a measurement the user changed goes onto: the one last sent for the position while the API has not answered it, or else the one the position holds.
	 *
	 * Two measurements changed one after the other are the same parameter changed twice. The second interrupts the first, and goes out on top of what the first sent — see #updateParameter — but in place of the geometry the first sent rather than beside it. Made from the geometry on show, it would carry the first
	 * measurement's old length back, and the first change would be lost.
	 *
	 * A shape picked and not yet answered is not a geometry to put a measurement on: it has no measurements until the engine has worked them out, and the fields are hidden while the dialog shows it. They come back if the user picks the position's shape back before the answer, and a measurement changed then goes on the
	 * geometry on show, in place of the pick. That takes the pick back, which is what picking the shape back asked for: what the user did last is what the position ends up holding.
	 */
	#geometryToChange(position: ModelPosition, geometry: GeometryParameter): GeometryParameter {
		const sent = this.#sentItems.get(position.id)?.find((parameter) => parameter.self === geometry.self);
		return sent instanceof GeometryParameter ? sent : geometry;
	}

	/**
	 * Takes back, on the fields of every length the refused geometry gave its measurements, what they said, if the position is still the one in the dialog.
	 *
	 * Every one, and not only the one changed last: measurements changed one after the other go out together — see #geometryToChange — so a refusal is of all of them, and none of them is on its way any more. A field not told would go on measuring the next change against a length the position does not have. The fields
	 * are not marked: what went wrong is on the position by now — see #refuse — and the dialog shows it where the drawing was.
	 *
	 * Through the dialog's measurements, which hold a field for each measurement; the machine holds no fields. A position the user has since left has its own lengths in those fields now, and a refusal of a length nobody can see any more is shown to nobody.
	 */
	#rejectMeasurement(position: ModelPosition, held: GeometryParameter, refused: GeometryParameter): void {
		if (ConfiguratorModel.currentPosition.value?.id !== position.id) {
			return;
		}
		const lengths = measurementsOf(held.value);
		for (const { measurement, value } of measurementsOf(refused.value)) {
			if (lengths.find((length) => length.measurement === measurement)?.value !== value) {
				this.configurator.geometryOverlay.dialog.measurements.field(measurement).rejectValue();
			}
		}
	}

	/**
	 * The model position whose configuration is drawing that parameter, or null when none is.
	 *
	 * By identity, because the rows are filled from the very objects the configuration holds — see #showParameters, which copies the items and not their contents. Null is the ordinary case for a parameter the user has since left behind: the rows are pooled and re-dealt, and a change that lands after the position was
	 * swapped out or the selection moved on names an object no position holds any more.
	 */
	#positionHolding(parameter: IConfigurationParameter): { readonly configuration: Configuration; readonly position: ModelPosition } | null {
		for (const position of ConfiguratorModel.positions.collection) {
			if (position instanceof ModelPosition && position.configuration !== null && position.configuration.parameters.items.includes(parameter)) {
				return { configuration: position.configuration, position };
			}
		}
		return null;
	}

	/**
	 * Puts a refused side back on the row that gave it, if a row still is.
	 *
	 * The row is not marked, for a side or for a value: what went wrong is on the position by now — see #refuse — and the error card says it where the user can read it, which a mark on a control cannot. A refused value is left in its row to be corrected, which asks nothing of the row and one thing of #showParameters:
	 * that the refused position becoming the selection again, as a replaced position does, re-deals no rows. A refused side is put back, since the dropdown would otherwise go on showing a side the parameter does not have: see AnswerItemRenderer.rejectSide.
	 *
	 * Through the list, which knows which row is drawing which parameter; the machine holds no rows. Undefined is a row that has moved on — the rows are pooled and re-dealt when the selection changes — and a side nobody can see any more is put back for nobody.
	 */
	#rejectAnswer(event: AnswerItemRendererEvent): void {
		if (event.type !== AnswerItemRendererEvent.SIDE_CHANGED) {
			return;
		}
		this.configurator.answersSection.answersList.rendererFor(event.parameter)?.rejectSide();
	}

	/**
	 * Asks the API for a configuration with one parameter changed, and puts what comes back on the position, the drawing and the price included.
	 *
	 * What a changed answer, a picked shape and a changed measurement have in common; they differ only in what is changed and in what a refusal looks like, so both are handed in.
	 *
	 * Built again around the changed parameter rather than patched: a configuration is a value, like a position, and the whole set of parameters goes over — see `ConfigurationBody` for why the whole set. What the API makes from them is a configuration of its own, at a path of its own. That is what is written over the
	 * position, because the API may also have moved parameters the user never touched; the rows are then re-dealt from it, if it is the position being looked at.
	 *
	 * The set is the one last sent for the position when there is one, rather than the configuration it holds — see {@link ConfiguratorMachine.sentItems}. This runs as the position's work, so a change made before the last one has answered interrupts it, and what the interrupted one sent is only kept by building this
	 * one on top of it. The parameter changed is matched by its path rather than by identity for the same reason: in the set last sent, it may already be a changed copy of the one the row shows.
	 *
	 * The parameters go to the API apart from the configuration they were read off, because a changed one may be a kind no configuration holds — a geometry picked for the engine to measure. See `createConfigurationFrom`.
	 *
	 * A parameter nobody has answered is kept in the set and left out of what is sent — see `answeredOf`. Kept, so that answering it later finds it in the set last sent, where the answer takes its place; left out, because the engine does not take its own open question back. The engine asks it again in its answer, so
	 * the row goes on showing it until the user answers it.
	 */
	#updateParameter(position: ModelPosition, configuration: Configuration, current: IConfigurationParameter, changed: ISentConfigurationParameter, rejected: () => void): Effect.Effect<void, never, ApiServices> {
		const sent = this.#sentItems.get(position.id) ?? configuration.parameters.items;
		const items = sent.map((parameter): ISentConfigurationParameter => (parameter.self === current.self ? changed : parameter));
		return this.#reconfigure(position, configuration, items, rejected);
	}

	/**
	 * Asks the API for a configuration of the position as it now measures, with the answers last sent for it, and puts what comes back on the position, the price included.
	 *
	 * How a width, a height or a quantity changed in the position's row is taken. The answers are the ones last sent while the API has not answered them, or else the configuration's own, for the reason {@link ConfiguratorMachine.updateParameter} gives: a change interrupted before the API answered is in the set last
	 * sent and in nothing else. The measurements are read off the position, where the row's change has already been written — see positionChanged.
	 */
	#remeasure(position: ModelPosition, configuration: Configuration): Effect.Effect<void, never, ApiServices> {
		const items = this.#sentItems.get(position.id) ?? configuration.parameters.items;
		return this.#reconfigure(position, configuration, items, () => {});
	}

	/** Sends a position's answers, measured as the position now is, and takes what the API makes of them: what a changed answer and a changed measurement have in common. See {@link ConfiguratorMachine.updateParameter} for how, and #refuse for what a refusal leaves. */
	#reconfigure(position: ModelPosition, configuration: Configuration, items: ReadonlyArray<ISentConfigurationParameter>, rejected: () => void): Effect.Effect<void, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			this.#sentItems.set(position.id, items);
			const held = yield* this.#attempting('configuration', position, createConfigurationFromEffect(position, configuration, answeredOf(items)));
			// answered either way, so the next change starts from what the position holds now: the new
			// configuration, or — refused — the one it had
			this.#sentItems.delete(position.id);
			if (Result.isFailure(held)) {
				// the position keeps everything it had, its price and its configuration included, and
				// carries the refusal beside them until an answer goes through for it
				this.#refuse(position, configuration, held.failure);
				rejected();
				return;
			}
			// pending, naming no calculation and carrying no refusal, because the price the position
			// holds was for the answers it had before, and a refusal it carries was of an answer this one
			// replaces: the row starts turning again the moment the answer has been taken, and comes to
			// rest on whatever the engine says the changed position costs. The drawing is left up
			// meanwhile — a stale drawing says more than none, and #pricePosition swaps it for the new
			// one when the price lands
			const answered = new ModelPosition({ ...position, calculationId: null, configuration: held.success, error: null, status: 'pending' });
			// a configuration the engine will not price leaves the position with no price and no
			// measurements: the ones it had were for answers it no longer has, and would otherwise stand
			// for as long as the question stays open — see #priceable
			const updated = this.#priceable(answered) ? answered : new ModelPosition({ ...answered, bom: null, price: 0, totalPrice: 0 });
			ConfiguratorModel.positions.replace(position, updated);
			this.#showParametersIfSelected(updated);
			this.#showErrorIfSelected(updated);
			// one fiber from the change to the price, so a change made while this is being priced
			// interrupts the price too
			if (this.#priceable(updated)) {
				yield* this.#pricePosition(updated);
				return;
			}
			// nothing is on its way for the position, so what it shows is settled here: no measurements,
			// the way the panel shows a position nobody has priced
			if (ConfiguratorModel.currentPosition.value?.id === updated.id) {
				this.#showProperties(updated);
			}
		});
	}

	/**
	 * The parameter as the event changes it, or null when the event names a change it cannot have.
	 *
	 * An answer can be given on any kind. A side only on a lookup, and only as one of the sides there are: the row sends nothing else, so null here is an event made somewhere other than a row, and sending nothing is the answer to it — a configuration built around a side that is not one would be refused by the class
	 * before it was ever sent.
	 */
	#changedParameter(event: AnswerItemRendererEvent, parameter: IConfigurationParameter): IConfigurationParameter | null {
		if (event.type === AnswerItemRendererEvent.SIDE_CHANGED) {
			return parameter instanceof LookupParameter && isSideType(event.value) ? parameter.withSide(event.value) : null;
		}
		return this.#withAnswer(parameter, event.value);
	}

	/**
	 * The parameter with the user's answer on it, as the kind it is, or null when the kind takes no answer from a row.
	 *
	 * Four kinds, told apart the way the row tells them apart, and each made anew rather than changed: a parameter is a value, and the configuration it sits in is built again around it. Made by the classes themselves — see LookupParameter.withKey — which is where an answer becomes the parameter's own, in the state the
	 * engine's rule says: the one it had, or `default` for one that was pending.
	 *
	 * A lookup's value is a link, and the answer is a key — picked or typed, the row hands over the key either way. A key that would not make a link — nothing, from a field emptied and left — is a change the lookup cannot have, and is sent as nothing; see LookupParameter.withKey. A key the store has no value for makes
	 * a well-formed link the API then refuses, and that refusal is answered for in #updateParameter.
	 *
	 * A geometry is a shape and its measurements, and no text a row could hand over is one. Its row cannot be typed in, so null here is an event made somewhere other than a row, and sending nothing is the answer to it — the geometry dialog is what changes one.
	 */
	#withAnswer(parameter: IConfigurationParameter, value: string): IConfigurationParameter | null {
		if (parameter instanceof GeometryParameter) {
			return null;
		}
		if (parameter instanceof LookupParameter) {
			return parameter.withKey(value);
		}
		return parameter.withValue(value);
	}

	/**
	 * Writes what the user picked in the positions list to the model, and shows its drawing.
	 *
	 * De-selecting is a selection too: it puts null there, which says that no position is being worked on rather than that the last one still is — and takes the drawing off the screen with it, since a drawing of nothing is not a drawing.
	 *
	 * The drawing is left running for the same reason pricing is: a listener cannot be waited on, and nothing here needs the drawing except the model, which the fetch writes to.
	 */
	private positionSelectionChanged(event: PositionsListEvent): void {
		ConfiguratorModel.currentPosition.value = event.selectedPosition;
		this.#showParameters(event.selectedPosition);
		this.#showProperties(event.selectedPosition);
		this.#showError(event.selectedPosition);
		this.#showInProgress(event.selectedPosition);
		this.#showDrawing(event.selectedPosition);
	}

	/**
	 * Says whether a configuration or a calculation is out for the selected position, which is what the spinners wait on.
	 *
	 * The selected position's own calls and nobody else's: a spinner stands where that position's drawing and properties will be, and another position being worked up brings those no nearer. Nothing selected, and a text or stock position — which nothing is ever sent for — have none out.
	 *
	 * Read off the count by id, the way the selection is matched everywhere else: the position selected is the object the user picked, and the work replaces it with new ones as it goes.
	 */
	#showInProgress(position: IPositions | null): void {
		const id = position === null ? null : position.id;
		ConfiguratorModel.configurationInProgress.value = id !== null && this.#configurationsOut.has(id);
		ConfiguratorModel.calculationInProgress.value = id !== null && this.#calculationsOut.has(id);
	}

	/**
	 * Puts what the engine worked out about the selected position on screen, or takes whatever is there off it.
	 *
	 * A read rather than a fetch, for the reason #showParameters gives: a priced position is carrying its own BOM, so walking the list with a held arrow key asks the engine for nothing.
	 *
	 * Null covers everything that has nothing worked out: nothing selected, a text position, and a model or stock position nobody has priced yet. A position the engine turned down keeps the BOM of the price it still shows — see `PositionBom`.
	 */
	#showProperties(position: IPositions | null): void {
		ConfiguratorModel.currentProperties.value = isPriced(position) ? position.bom : null;
	}

	/**
	 * Shows what went wrong the last time the position was sent to the engine, or nothing for a position that is not sent anywhere.
	 *
	 * Read off the position the way {@link ConfiguratorMachine.showProperties} reads the BOM: a position carries its own error, so selecting one is a read. Null for a text position for the same reason the properties are — the engine is never sent one — and for a model or stock position whose last answer went through.
	 */
	#showError(position: IPositions | null): void {
		ConfiguratorModel.currentError.value = isPriced(position) ? position.error : null;
	}

	/**
	 * Shows what went wrong for a position if it is the one on screen, the way {@link ConfiguratorMachine.showParametersIfSelected} shows its parameters.
	 *
	 * Matched by id, because the position on screen is the object the user selected and this one has just replaced it.
	 */
	#showErrorIfSelected(position: PricedPosition): void {
		if (ConfiguratorModel.currentPosition.value?.id === position.id) {
			this.#showError(position);
		}
	}

	/**
	 * Writes a refusal to the position, which keeps everything else it had, measured as the configuration it still holds is.
	 *
	 * A new position rather than the old one changed, the way a price is written — a position is a value — and shown at once if it is the one on screen, since the two places that show a refusal read the model rather than the row. What it keeps is the point: its price, its configuration and its drawing are all still
	 * what the API holds for it, and the refusal sits beside them until an answer goes through, which replaces it with a position carrying none.
	 *
	 * The rows of the answers list are left as they are, the refused answer in its row among them: replacing the position makes the replacement the selection — see SelectList.itemReplaced — which brings its configuration back to #showParameters, and that is the configuration already on screen, so nothing is re-dealt.
	 */
	#refuse(position: ModelPosition, configuration: Configuration, message: string): void {
		// the measurements go back to the ones the configuration holds, which the API answered it with:
		// a change sent with other ones may have been a measurement of the user's own, written to the
		// position before it went — see positionChanged — and refused along with everything else
		const { height, quantity, width } = configuration;
		const refused = new ModelPosition({ ...position, error: message, height, quantity, width });
		ConfiguratorModel.positions.replace(position, refused);
		this.#showErrorIfSelected(refused);
	}

	/**
	 * Puts the selected position's parameters on screen, or takes whatever is there off it — unless what is there is these very parameters already.
	 *
	 * Nothing is fetched and nothing is waited for, unlike the drawing: a configured position is carrying its own configuration, so this is a read. That is why there is no gathering window here and no guard against an answer landing late — there is no answer on its way. Walking the list with a held arrow key reads
	 * eighteen configurations out of eighteen positions and asks the API for none of them.
	 *
	 * Empty covers everything that is not a configured model position: nothing selected, a text or stock position, one not configured yet, and one whose configuring failed.
	 *
	 * Nothing is written while the list shows these very parameters: the same objects, in the same order, and no others. A position is replaced for more than a new configuration — a price lands on it, a refusal is written to it, see #refuse — and the positions list says the replacement is the selection each time, see
	 * SelectList.itemReplaced, which brings the configuration already on screen back here. Identity catches every change there is to catch, since a configuration is a value: a changed one is a new one, with parameters of its own.
	 *
	 * A new configuration is reconciled with the one on screen rather than swapped in whole, matched by what each parameter is about — see `ConfigurationParameter.identity`. The answers list then keeps every row whose parameter is still there and hands it the parameter made anew, puts in a row for one that joined and
	 * takes out the row of one that left, and no row that stays leaves the document: a field the user has moved on to keeps its focus, and what they have typed in it stays theirs — see AnswerItemRenderer for what a row does with a parameter it is handed again. Swapped in whole, every row would be built again, which is
	 * what took the focus the moment an answer landed.
	 *
	 * Copied out of the configuration, because its items are readonly and the collection keeps what it is handed.
	 */
	#showParameters(position: IPositions | null): void {
		// before the answers rather than after, and whether or not they are already on screen: a
		// refusal replaces the position and leaves its configuration as it was, which writes the
		// same value and so leaves the row above the answers on the user's pick
		ConfiguratorModel.currentStockConfiguration.value = position instanceof StockPosition ? position.configuration : null;
		const configuration = isPriced(position) ? position.configuration : null;
		const parameters: ReadonlyArray<IPresentedParameter> = configuration === null ? [] : configuration.parameters.items;
		if (this.#showing(parameters)) {
			return;
		}
		ConfiguratorModel.currentParameters.reconcile([...parameters], (parameter) => parameter.identity);
	}

	/** Whether the answers list is showing exactly those parameters: the same objects, in the same order, and no others. See #showParameters for why that is asked. */
	#showing(parameters: ReadonlyArray<IPresentedParameter>): boolean {
		const shown = ConfiguratorModel.currentParameters.collection;
		return shown.length === parameters.length && shown.every((parameter, index) => parameter === parameters[index]);
	}

	/**
	 * Shows a worked-up position's parameters, if it is the one the user is looking at.
	 *
	 * A position can be selected before it has been configured, and its parameters only exist once it has: without this the user would sit looking at an empty answers list for a configuration that had just been made. Matched by id, because the position they selected is the object we have only just replaced — the same
	 * reason the drawing is matched that way.
	 */
	#showParametersIfSelected(position: PricedPosition): void {
		if (ConfiguratorModel.currentPosition.value?.id === position.id) {
			this.#showParameters(position);
		}
	}

	/**
	 * Writes the side of the window the user asked to see.
	 *
	 * Nothing is fetched: the engine draws both sides into every drawing and hides one, so turning it around is a matter of which pair of groups is shown. See `DrawingSide`.
	 */
	private drawingSideChanged(event: FrontBackToggleButtonBarEvent): void {
		ConfiguratorModel.currentDrawingSide.value = event.side;
	}

	/** Shows the geometry dialog the user asked for. */
	private showGeometryDialogButtonClicked(): void {
		ConfiguratorModel.geometryDialogShown.value = true;
	}

	/** Hides the properties panel if it is shown, and shows it if it is hidden: the one button does both, and the model is what says which it is time for. */
	private propertiesPanelShowHideButtonClicked(): void {
		ConfiguratorModel.propertiesPanelShown.value = !ConfiguratorModel.propertiesPanelShown.value;
	}

	/**
	 * Closes whichever dialog the pressed close button belongs to.
	 *
	 * Told apart by the button's dispatcher, because every CloseButton dispatches the same event type: the geometry dialog's button says `geometryDialog`, and a close button this machine knows nothing about is left for whoever does.
	 */
	private closeButtonClicked(event: CloseButtonEvent): void {
		if (event.dispatcher === 'geometryDialog') {
			ConfiguratorModel.geometryDialogShown.value = false;
		}
	}

	/**
	 * Says which drawing belongs on screen, and gathers a run of them into one.
	 *
	 * The first selection is acted on at once, so a single arrow press is as quick as it ever was. Every selection that follows within {@link DRAWING_BURST_WINDOW} only moves the target, and the drawing that is finally wanted is loaded when the run ends. Walking a list of eighteen positions with a held arrow key
	 * therefore loads two drawings rather than eighteen.
	 *
	 * Gathered for what it does to the screen more than for what it saves: without it the section changes at every step of the walk — a drawing, then nothing for a position the engine has not priced, then another drawing — where the point of walking the list is to arrive somewhere. Taking the drawing off the screen is
	 * gathered for the same reason, which is why nothing here looks at whether the position has a drawing at all.
	 *
	 * A run of eighteen also moved seven hundred kilobytes and made eighteen requests of a CDN. That is the smaller reason, and it was not the one measured to matter: eighteen at once cost 74 ms against 16 ms for one alone, which nobody would notice. A slow connection would.
	 *
	 * The target moving away from what was loaded at the start of the window interrupts that load: it is no longer wanted, and left to land it would put the wrong drawing on screen. What is wanted when the window closes is loaded then.
	 */
	#showDrawing(position: IPositions | null): void {
		// a stopped machine draws nothing more: a price landing after the stop would otherwise open
		// the window again and fetch for a screen that is gone — see stop()
		if (this.#stopped) {
			return;
		}
		this.#wantedDrawingPosition = position;
		if (this.#drawingWindowOpen) {
			if (position !== this.#loadedDrawingPosition) {
				this.#runtime.runFork(FiberHandle.clear(this.#drawingLoad));
				this.#loadedDrawingPosition = undefined;
			}
			this.#openDrawingWindow();
			return;
		}
		this.#openDrawingWindow();
		this.#loadedDrawingPosition = position;
		this.#runtime.runFork(FiberHandle.run(this.#drawingLoad, this.#loadDrawing(position)));
	}

	/** Opens the window, or keeps it open for another {@link DRAWING_BURST_WINDOW}: the window fiber run before is interrupted by this one. */
	#openDrawingWindow(): void {
		this.#drawingWindowOpen = true;
		this.#runtime.runFork(FiberHandle.run(this.#drawingWindow, Effect.sleep(DRAWING_BURST_WINDOW).pipe(Effect.andThen(Effect.suspend(() => this.#closeDrawingWindow())))));
	}

	/** Closes the window, and loads what is wanted by now, unless it is what was loaded when the window opened. */
	#closeDrawingWindow(): Effect.Effect<void, never, ApiServices> {
		this.#drawingWindowOpen = false;
		if (this.#wantedDrawingPosition === this.#loadedDrawingPosition) {
			return Effect.void;
		}
		this.#loadedDrawingPosition = this.#wantedDrawingPosition;
		return FiberHandle.run(this.#drawingLoad, this.#loadDrawing(this.#wantedDrawingPosition)).pipe(Effect.asVoid);
	}

	/**
	 * Puts the drawing of one position on the screen, or takes whatever is there off it.
	 *
	 * A drawing exists only for a model position the engine has priced — see `ModelPosition.drawingId`, which is where that is decided — so everything else is answered with nothing: a text position, a stock position, one still being worked out, one the engine turned down. A fetch that fails is answered the same way,
	 * which is the one thing here worth knowing about: an empty section says less than it should, and it is what the model says it says for now.
	 *
	 * Run in {@link ConfiguratorMachine.drawingLoad}, so a load that is no longer wanted is interrupted rather than asked whether it still is: nothing after the fetch runs, and nothing is written.
	 */
	#loadDrawing(position: IPositions | null): Effect.Effect<void, never, ApiServices> {
		const drawingId = position instanceof ModelPosition ? position.drawingId : null;
		if (drawingId === null) {
			return Effect.sync(() => {
				ConfiguratorModel.currentDrawing.value = null;
			});
		}
		return Effect.gen(function* () {
			// Both at once rather than one after the other: the stylesheets and the drawing come from
			// the same origin and neither needs the other to be asked for, so waiting for one before
			// asking for the other would cost a round trip for nothing. Both are read from caches after
			// the first drawing, so this settles at once from then on. Each is held as a result, so one
			// failing does not take the other with it.
			const [styles, markup] = yield* Effect.all([Effect.result(getDrawingStylesEffect()), Effect.result(getDrawingEffect(drawingId))], { concurrency: 'unbounded' });
			// Stylesheets that could not be read leave the model holding what it had — the supplement
			// alone, which is enough to show a drawing in the wrong colours. That is worse than the
			// right colours and better than an empty section, so a drawing is not held back for them.
			const sheets = Result.getOrNull(styles);
			if (sheets !== null) {
				ConfiguratorModel.currentDrawingStyles.value = sheets;
			}
			const svg = Result.getOrNull(markup);
			ConfiguratorModel.currentDrawing.value = svg === null ? null : Result.getOrNull(parseDrawing(svg));
		});
	}

	/**
	 * Works a new position up: configured first, then priced.
	 *
	 * In that order, and serially, for three reasons. A model the store does not have is the likeliest thing to be wrong with a new position, and configuring finds it out in one round trip rather than after the minute a calculation may take. Configuring is also what produces the parameters the position is built from,
	 * which is the point of having it at all. And doing them one after the other means only one answer is ever on its way back for a position, so neither can arrive to find the other has already taken its place.
	 *
	 * The calculation names the configuration it prices by, so the order buys a fourth thing: there is a configuration to name by the time the engine is asked. A position priced before it was configured would be refused — see `UnconfiguredPositionError` — and one priced without naming it would be priced as the model's
	 * defaults, which is what a changed answer is exactly not.
	 */
	#workUpPosition(position: ModelPosition): Effect.Effect<void, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			const configured = yield* this.#configurePosition(position);
			if (configured !== null && this.#priceable(configured)) {
				yield* this.#pricePosition(configured);
			}
		});
	}

	/**
	 * Whether the engine will price the position: it holds a configuration, and the engine calls it complete.
	 *
	 * A configuration the engine calls pending has a parameter nobody has answered, and the engine does not price one: asked, it would refuse, and the position would read as failed when it is only waiting on an answer. So it is not asked. The position is left without a price and without measurements — the ones it had
	 * were for answers it no longer has, and a question can stay open for a long time — and its row says it is waiting on an answer, see `PositionItemRenderer`, until an answer makes the configuration complete, which prices it like any other change. The drawing is left up meanwhile, the way it is while a price is on
	 * its way: a stale drawing says more than none.
	 */
	#priceable(position: PricedPosition): boolean {
		return position.configuration?.statusType === 'complete';
	}

	/**
	 * Works a new stock position up: configured as the item it is, and as many of it as the position is for.
	 *
	 * As the item alone: the engine picks none of the configurations the item comes in, and answers with the list they are picked from instead, so the configuration waits on that pick the way it waits on an answer — see {@link ConfiguratorMachine.itemConfigurationChanged}. An item that comes in exactly one has nothing
	 * to pick between, so that one is picked for the user, as if they had — see #onlyItemConfiguration — and its defaults are answered after the pick rather than before, since the questions of the item alone are not the ones the user goes on to answer.
	 *
	 * Not priced, unlike a model: the engine cannot price a stock item yet, so the calculations are never asked about one. A configuration the engine calls complete is the end of the work — see #settleStock — and one it calls pending, with a question nobody has answered yet like the model width a blind is made to,
	 * waits on its answers, the row saying so.
	 */
	#workUpStockPosition(position: StockPosition): Effect.Effect<void, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			const configured = yield* this.#configureStockPosition(position);
			if (configured !== null) {
				yield* this.#pickOnlyItemConfigurationOr(configured.position, configured.configuration, this.#answerDefaultsOrSettle(configured.position, configured.configuration));
			}
		});
	}

	/**
	 * Picks the one configuration the item comes in for the user, when it comes in exactly one, and does `otherwise` when there is a pick left for the user to make or none to make at all.
	 *
	 * The listing is remembered until it has been answered, so a change to the row that interrupts it lists again rather than dropping the pick — see #workAfterChange.
	 */
	#pickOnlyItemConfigurationOr(position: StockPosition, configuration: StockConfiguration, otherwise: Effect.Effect<void, never, ApiServices>): Effect.Effect<void, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			this.#listingItemConfigurations.add(position.id);
			const only = yield* this.#onlyItemConfiguration(position, configuration);
			this.#listingItemConfigurations.delete(position.id);
			yield* only === null ? otherwise : this.#pickItemConfiguration(position, configuration, only);
		});
	}

	/**
	 * The code of the one configuration the item comes in — `K001` — when it comes in exactly one, or null when there is a pick for the user to make or none to make at all.
	 *
	 * Listed from the link the configuration names, through the same cache the dropdown above the answers lists from, so the dropdown is answered without asking again. Counted as configuration work, so the position reads as in progress from the item's configuration through to the one picked for it.
	 *
	 * A list that cannot be fetched is no reason to fail the position: it is left waiting on the user's pick, as it is for an item with more than one, and the dropdown tries the list again.
	 */
	#onlyItemConfiguration(position: StockPosition, configuration: StockConfiguration): Effect.Effect<string | null, never, ApiServices> {
		const source = configuration.itemConfigurationsList;
		if (source === null || configuration.itemConfigurationKey !== null) {
			return Effect.succeed(null);
		}
		return this.#counting('configuration', position, listCached(source)).pipe(
			Effect.map(([first, ...rest]) => (first !== undefined && rest.length === 0 ? first.key : null)),
			Effect.catch(() => Effect.succeed(null)),
		);
	}

	/**
	 * Answers the questions a fresh stock configuration left open with the defaults the engine names for them, or puts the position to rest when there are none.
	 *
	 * A question the engine left open but names a default for is answered with that default, so the configuration need not wait on the user for an answer the engine already knows; asked again, it is settled the way any answer is — see #reconfigureStock. Fresh is a configuration just made as the item, or just made
	 * again in another of the configurations it comes in: its questions are new, and nobody has answered them.
	 */
	#answerDefaultsOrSettle(position: StockPosition, configuration: StockConfiguration): Effect.Effect<void, never, ApiServices> {
		const defaulted = withDefaultAnswers(configuration.parameters.items);
		if (defaulted !== null) {
			return this.#reconfigureStock(position, configuration, defaulted);
		}
		return Effect.sync(() => {
			this.#settleStock(position);
		});
	}

	/**
	 * Puts a configured stock position to rest, without pricing it, once the engine calls its configuration complete.
	 *
	 * The engine cannot price a stock item yet, so a complete configuration is as far as the work goes: the position is marked done, at the nought it was handed over at, rather than left pending and turning for a price nobody will ask for. One the engine calls pending is left as it is, waiting on its answers.
	 */
	#settleStock(position: StockPosition): void {
		if (!this.#priceable(position)) {
			return;
		}
		ConfiguratorModel.positions.replace(position, new StockPosition({ ...position, status: 'succeeded' }));
	}

	/**
	 * Asks the API to configure a stock position, and answers with it carrying its configuration — or null, once it has been marked failed, the way {@link ConfiguratorMachine.configurePosition} answers for a model.
	 *
	 * The configuration is answered beside the position as well as on it, so a caller reads it without asking again whether the position holds one.
	 *
	 * As the item alone, naming none of the configurations it comes in — see {@link ConfiguratorMachine.workUpStockPosition}. An item the store does not have is found out here.
	 */
	#configureStockPosition(position: StockPosition): Effect.Effect<{ readonly configuration: StockConfiguration; readonly position: StockPosition } | null, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			const configuration = yield* this.#attempting('configuration', position, createStockConfigurationEffect({ itemConfiguration: null, key: position.key, quantity: position.quantity }));
			if (Result.isFailure(configuration)) {
				const failed = new StockPosition({ ...position, calculationId: null, configuration: null, error: configuration.failure, status: 'failed' });
				ConfiguratorModel.positions.replace(position, failed);
				this.#showParametersIfSelected(failed);
				this.#showErrorIfSelected(failed);
				return null;
			}
			// pending and with no error, whatever the position carried: it may be a retry of one whose
			// configuring failed, and that refusal is answered now
			const configured = new StockPosition({ ...position, configuration: configuration.success, error: null, status: 'pending' });
			ConfiguratorModel.positions.replace(position, configured);
			// the item's questions are what the user answers, so they are on screen as soon as they exist
			this.#showParametersIfSelected(configured);
			this.#showErrorIfSelected(configured);
			return { configuration: configuration.success, position: configured };
		});
	}

	/**
	 * Asks the API to configure a position, and answers with it carrying its configuration.
	 *
	 * Null means it could not be configured and the position has been marked failed, so the caller stops rather than pricing something the store may not even have.
	 *
	 * The configuration id is written to the position as soon as it is known, rather than being carried along and written once the price arrives with it. A calculation may be interrupted, and a configuration that went the same way would leave the API holding one nothing points at.
	 */
	#configurePosition(position: ModelPosition): Effect.Effect<ModelPosition | null, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			const configuration = yield* this.#attempting('configuration', position, createConfigurationEffect(position));
			if (Result.isFailure(configuration)) {
				// no calculation to name, because none was ever asked for — and what stopped it, in words,
				// since a row marked failed says that something did and not what
				const failed = new ModelPosition({
					...position,
					calculationId: null,
					configuration: null,
					error: configuration.failure,
					status: 'failed',
				});
				ConfiguratorModel.positions.replace(position, failed);
				this.#showParametersIfSelected(failed);
				this.#showErrorIfSelected(failed);
				return null;
			}
			// pending and with no error, whatever the position carried: a position whose configuring failed
			// is worked up again from the start when it is measured anew — see #workAfterChange — and that
			// refusal is answered now. Left on it, a configuration still waiting on an answer would never
			// be priced to clear it, and the row would go on saying failed
			const configured = new ModelPosition({ ...position, configuration: configuration.success, error: null, status: 'pending' });
			ConfiguratorModel.positions.replace(position, configured);
			this.#showParametersIfSelected(configured);
			this.#showErrorIfSelected(configured);
			return configured;
		});
	}

	/**
	 * Asks the engine what a position costs, and puts the answer where the row will see it.
	 *
	 * Part of the position's work, so a change to the position while the engine works on it interrupts it: the engine is no longer asked after a price for answers the position no longer has, and nothing is written. A calculation the engine has already taken on finishes there, unread.
	 *
	 * The answer is put back only over the very position it was asked about, which replace() does by doing nothing for an item the collection no longer holds.
	 *
	 * Every way this can end, short of being interrupted, leaves the position saying where it got to. A calculation the engine could not make comes back as a position that says so, naming the calculation it turned down; a call that never reached the engine is marked failed here, and names none, because there is no
	 * calculation to name. The two are not the same thing, and one day the row will say which — but a row that goes on turning for a calculation nobody is working on is worse than a row that says the wrong sort of failure.
	 *
	 * The configuration made a moment ago is kept through both endings, because the spread carries it: a position the engine could not price still has a configuration, and losing it would leave one behind on the API.
	 *
	 * A model position alone: the calculations can price a stock position as well, but the engine cannot price a stock item yet, so none is sent — see #settleStock.
	 */
	#pricePosition(position: ModelPosition): Effect.Effect<void, never, ApiServices> {
		return Effect.gen({ self: this }, function* () {
			const priced = yield* this.#attempting('calculation', position, createCalculationEffect(position));
			// a calculation the engine turned down is an answer, and comes back as a position that says
			// why in the engine's words — see `priced` in the calculations service; a call that never got
			// that far is marked here, and says why in ours
			const replacement = Result.isSuccess(priced) ? priced.success : new ModelPosition({ ...position, calculationId: null, error: priced.failure, status: 'failed' });
			ConfiguratorModel.positions.replace(position, replacement);
			// A position can be selected before it has been priced, and its drawing and its measurements
			// only exist once it has: without this the user would sit looking at an empty section and an
			// empty panel for a price that had just landed. Matched by id, because the position they
			// selected is the object we have only just replaced.
			if (ConfiguratorModel.currentPosition.value?.id === replacement.id) {
				this.#showProperties(replacement);
				this.#showDrawing(replacement);
				this.#showError(replacement);
			}
		});
	}

	/**
	 * Runs work for a position, interrupting whatever work that position already has under way.
	 *
	 * Forked in the Configurator's runtime, into {@link ConfiguratorMachine.positionWork} under the position's id, which is what does the interrupting. Nothing waits on the fiber: the work writes to the model, and the model is what everything else reads.
	 */
	#runForPosition(position: IPositions, work: Effect.Effect<void, never, ApiServices>): void {
		this.#runtime.runFork(FiberMap.run(this.#positionWork, position.id, work));
	}

	/**
	 * Runs a call while counting it among the ones of its kind that are out for the position, and keeps the model's flag for that kind in step.
	 *
	 * Counted rather than set and cleared by each call, because more than one can be out for a position at once: a call overtaken by the next is interrupted, and the next may be counted before the first has been taken off. Taken off the count however the call ends: with an answer, with a failure, or interrupted
	 * because it was overtaken.
	 */
	#counting<A, E, R>(kind: 'calculation' | 'configuration', position: IPositions, call: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> {
		return Effect.acquireUseRelease(
			Effect.sync(() => {
				this.#count(kind, position.id, 1);
			}),
			() => call,
			() =>
				Effect.sync(() => {
					this.#count(kind, position.id, -1);
				}),
		);
	}

	/**
	 * Runs a call, counted, and answers with what it gave or with what to tell the user it failed with.
	 *
	 * A Result rather than a failure, because the three callers all go on after one — a refused answer is written to the position and the work is done — and what they go on with is the sentence, not the error: by the time it is a sentence the error has been read for everything it is worth, the body of a refusal
	 * included. See `failureMessage` for the sentences.
	 */
	#attempting<A, E extends PositionCallError, R>(kind: 'calculation' | 'configuration', position: IPositions, call: Effect.Effect<A, E, R>): Effect.Effect<Result.Result<A, string>, never, R> {
		return this.#counting(kind, position, call).pipe(
			Effect.map((answer): Result.Result<A, string> => Result.succeed(answer)),
			Effect.catch((error): Effect.Effect<Result.Result<A, string>> => failureMessage(error).pipe(Effect.map((message) => Result.fail(message)))),
		);
	}

	/** Moves the count of one kind of call for a position, and writes whether any are out for the selected one and, for a configuration, which positions have one out. */
	#count(kind: 'calculation' | 'configuration', id: string, by: number): void {
		const out = kind === 'configuration' ? this.#configurationsOut : this.#calculationsOut;
		const was = out.has(id);
		const count = (out.get(id) ?? 0) + by;
		if (count > 0) {
			out.set(id, count);
		} else {
			out.delete(id);
		}
		if (kind === 'configuration' && out.has(id) !== was) {
			ConfiguratorModel.positionsConfiguring.value = new Set(out.keys());
		}
		this.#showInProgress(ConfiguratorModel.currentPosition.value);
	}
}
